import React from 'react';
import Grid from '@mui/material/Grid2';
import Skeleton from '@mui/material/Skeleton';
import { useTheme, Breakpoint } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

export type ResponsiveCount = Partial<Record<Breakpoint, number>>;

type Props = {
  loading: boolean;
  columns: ResponsiveCount;
  rows: ResponsiveCount;
  skeletonHeight?: number | string;
  skeletonWidth?: number | string;
  spacing?: number;
  children: React.ReactNode;
  errEl?: React.ReactNode;
  isError?: boolean;
};

const SkeletonContainer = (props: Props) => {
  const {
    loading,
    columns,
    rows,
    skeletonHeight = 150,
    skeletonWidth = '100%',
    spacing = 1,
    children,
    errEl,
    isError = false,
  } = props;
  const theme = useTheme();
  const keys = theme.breakpoints.keys;
  const bpsDesc = [...keys].reverse();

  const matches = {
    xs: useMediaQuery(theme.breakpoints.up('xs')),
    sm: useMediaQuery(theme.breakpoints.up('sm')),
    md: useMediaQuery(theme.breakpoints.up('md')),
    lg: useMediaQuery(theme.breakpoints.up('lg')),
    xl: useMediaQuery(theme.breakpoints.up('xl')),
  };

  if (isError && !loading && errEl) {
    return <>{errEl}</>;
  }

  if (!loading) return <>{children}</>;

  // Determine active breakpoint (highest matched)
  const current: Breakpoint = bpsDesc.find(bp => matches[bp]) || 'xs';

  // Resolve value at current breakpoint, falling back descending
  const resolve = (counts: ResponsiveCount): number => {
    if (counts[current] != null) return counts[current]!;
    for (const bp of bpsDesc) {
      if (counts[bp] != null) return counts[bp]!;
    }
    return 0;
  };

  const cols = resolve(columns);
  const rowsCount = resolve(rows);
  const total = cols * rowsCount;

  // Build Grid2 size prop for active breakpoint
  const sizeProp: Partial<Record<Breakpoint, number>> = {};
  if (cols > 0) sizeProp[current] = 12 / cols;

  const skeletons = Array.from({ length: total }, (_, idx) => (
    <Grid key={idx} size={sizeProp}>
      <Skeleton variant="rounded" width={skeletonWidth} height={skeletonHeight} animation="wave" />
    </Grid>
  ));

  return (
    <Grid container spacing={spacing} sx={{ width: '100%' }}>
      {skeletons}
    </Grid>
  );
};

export default SkeletonContainer;
